import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAuth } from '../hooks/useAuth';
import {
  getBedrockMetrics,
  type BedrockMetricsResponse,
  type ModelConfig,
} from '../api/metrics';

// ---------------------------------------------------------------------------
// Time range presets
// ---------------------------------------------------------------------------

interface TimePreset {
  label: string;
  hours: number;
  period: number;
}

const TIME_PRESETS: TimePreset[] = [
  { label: '15m', hours: 0.25, period: 60 },
  { label: '30m', hours: 0.5, period: 60 },
  { label: '1h', hours: 1, period: 60 },
  { label: '6h', hours: 6, period: 300 },
  { label: '24h', hours: 24, period: 300 },
  { label: '3d', hours: 72, period: 900 },
  { label: '7d', hours: 168, period: 3600 },
];

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatLatency(ms: number): string {
  if (ms >= 60_000) return `${(ms / 60_000).toFixed(1)}m`;
  if (ms >= 1_000) return `${(ms / 1_000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function formatTime(iso: string, hours: number): string {
  const d = new Date(iso);
  if (hours <= 1) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  if (hours <= 24) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return `${d.getMonth() + 1}/${d.getDate()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

// ---------------------------------------------------------------------------
// Dark-themed tooltip style (matches Flux UI)
// ---------------------------------------------------------------------------

const TOOLTIP_STYLE = {
  background: '#0c1526',
  border: '1px solid #1e2d4a',
  borderRadius: 6,
  fontSize: 12,
};

const AXIS_TICK = { fill: '#64748b', fontSize: 11 };
const AXIS_LINE = { stroke: '#1e2d4a' };

// ---------------------------------------------------------------------------
// Build unified time-series data for Recharts
// ---------------------------------------------------------------------------

interface ChartPoint {
  time: string;
  [key: string]: string | number;
}

function buildChartData(
  data: BedrockMetricsResponse,
  metricName: string,
): ChartPoint[] {
  // Collect all unique timestamps across models
  const tsSet = new Set<string>();
  data.models.forEach((modelId) => {
    const series = data.metrics[modelId]?.[metricName as keyof typeof data.metrics[string]];
    if (series) {
      series.timestamps.forEach((ts) => tsSet.add(ts));
    }
  });

  const sortedTs = Array.from(tsSet).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  return sortedTs.map((ts) => {
    const point: ChartPoint = { time: ts };
    data.models.forEach((modelId) => {
      const series = data.metrics[modelId]?.[metricName as keyof typeof data.metrics[string]];
      if (series) {
        const idx = series.timestamps.indexOf(ts);
        point[modelId] = idx >= 0 ? series.values[idx] : 0;
      } else {
        point[modelId] = 0;
      }
    });
    return point;
  });
}

function buildTokenChartData(data: BedrockMetricsResponse): ChartPoint[] {
  const tsSet = new Set<string>();
  data.models.forEach((modelId) => {
    const input = data.metrics[modelId]?.InputTokenCount;
    const output = data.metrics[modelId]?.OutputTokenCount;
    if (input) input.timestamps.forEach((ts) => tsSet.add(ts));
    if (output) output.timestamps.forEach((ts) => tsSet.add(ts));
  });

  const sortedTs = Array.from(tsSet).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  return sortedTs.map((ts) => {
    const point: ChartPoint = { time: ts };
    data.models.forEach((modelId) => {
      const inputSeries = data.metrics[modelId]?.InputTokenCount;
      const outputSeries = data.metrics[modelId]?.OutputTokenCount;
      const inputIdx = inputSeries?.timestamps.indexOf(ts) ?? -1;
      const outputIdx = outputSeries?.timestamps.indexOf(ts) ?? -1;
      point[`${modelId}_input`] = inputIdx >= 0 ? (inputSeries?.values[inputIdx] ?? 0) : 0;
      point[`${modelId}_output`] = outputIdx >= 0 ? (outputSeries?.values[outputIdx] ?? 0) : 0;
    });
    return point;
  });
}

function buildErrorChartData(data: BedrockMetricsResponse): ChartPoint[] {
  const tsSet = new Set<string>();
  data.models.forEach((modelId) => {
    const throttles = data.metrics[modelId]?.InvocationThrottles;
    const clientErr = data.metrics[modelId]?.InvocationClientErrors;
    const serverErr = data.metrics[modelId]?.InvocationServerErrors;
    if (throttles) throttles.timestamps.forEach((ts) => tsSet.add(ts));
    if (clientErr) clientErr.timestamps.forEach((ts) => tsSet.add(ts));
    if (serverErr) serverErr.timestamps.forEach((ts) => tsSet.add(ts));
  });

  const sortedTs = Array.from(tsSet).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  return sortedTs.map((ts) => {
    const point: ChartPoint = { time: ts };
    data.models.forEach((modelId) => {
      const throttles = data.metrics[modelId]?.InvocationThrottles;
      const clientErr = data.metrics[modelId]?.InvocationClientErrors;
      const serverErr = data.metrics[modelId]?.InvocationServerErrors;
      const tIdx = throttles?.timestamps.indexOf(ts) ?? -1;
      const cIdx = clientErr?.timestamps.indexOf(ts) ?? -1;
      const sIdx = serverErr?.timestamps.indexOf(ts) ?? -1;
      point[`${modelId}_throttles`] = tIdx >= 0 ? (throttles?.values[tIdx] ?? 0) : 0;
      point[`${modelId}_errors`] =
        (cIdx >= 0 ? (clientErr?.values[cIdx] ?? 0) : 0) +
        (sIdx >= 0 ? (serverErr?.values[sIdx] ?? 0) : 0);
    });
    return point;
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MetricsView() {
  const { getJwtToken } = useAuth();

  const [data, setData] = useState<BedrockMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState(4); // default 24h
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const preset = TIME_PRESETS[selectedPreset];
      const result = await getBedrockMetrics(token, preset.hours, preset.period);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken, selectedPreset]);

  // Fetch on mount and when preset changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh toggle
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, 60_000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, fetchData]);

  // Compute totals across all models
  const totalInvocations = data
    ? data.models.reduce((acc, m) => acc + (data.summary[m]?.invocations ?? 0), 0)
    : 0;
  const totalInput = data
    ? data.models.reduce((acc, m) => acc + (data.summary[m]?.inputTokens ?? 0), 0)
    : 0;
  const totalOutput = data
    ? data.models.reduce((acc, m) => acc + (data.summary[m]?.outputTokens ?? 0), 0)
    : 0;
  const totalThrottles = data
    ? data.models.reduce((acc, m) => acc + (data.summary[m]?.throttles ?? 0), 0)
    : 0;
  const totalErrors = data
    ? data.models.reduce((acc, m) => acc + (data.summary[m]?.errors ?? 0), 0)
    : 0;

  const preset = TIME_PRESETS[selectedPreset];
  const configMap = new Map<string, ModelConfig>(
    (data?.modelConfig ?? []).map((c) => [c.modelId, c]),
  );

  return (
    <div>
      {/* Header */}
      <div className="view__header">
        <h1 className="view__title">Bedrock Metrics</h1>
        <div className="view__actions">
          {/* Time presets */}
          {TIME_PRESETS.map((p, idx) => (
            <button
              key={p.label}
              type="button"
              className={`btn btn--sm ${idx === selectedPreset ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setSelectedPreset(idx)}
            >
              {p.label}
            </button>
          ))}

          {/* Divider */}
          <span style={{ width: 1, height: 24, background: '#1e2d4a', margin: '0 4px' }} />

          {/* Auto-refresh toggle */}
          <button
            type="button"
            className={`btn btn--sm ${autoRefresh ? 'btn--green' : 'btn--secondary'}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? 'Auto-refresh ON (60s)' : 'Auto-refresh OFF'}
          >
            {autoRefresh ? 'Auto: ON' : 'Auto: OFF'}
          </button>

          {/* Manual refresh */}
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="view__empty">Loading Bedrock metrics...</div>
      )}

      {/* KPI Cards */}
      {data && (
        <div className="dashboard-kpis">
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value" style={{ color: '#fff' }}>
              {formatNumber(totalInvocations)}
            </span>
            <span className="dashboard-kpi__label">Total Invocations</span>
          </div>
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value" style={{ color: '#00d4ff' }}>
              {formatNumber(totalInput)}
            </span>
            <span className="dashboard-kpi__label">Input Tokens</span>
          </div>
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value" style={{ color: '#8b5cf6' }}>
              {formatNumber(totalOutput)}
            </span>
            <span className="dashboard-kpi__label">Output Tokens</span>
          </div>
          <div className="dashboard-kpi">
            <span
              className="dashboard-kpi__value"
              style={{ color: totalThrottles > 0 ? '#ffab00' : undefined }}
            >
              {formatNumber(totalThrottles)}
            </span>
            <span className="dashboard-kpi__label">Throttles</span>
          </div>
          <div className="dashboard-kpi">
            <span
              className="dashboard-kpi__value"
              style={{ color: totalErrors > 0 ? '#ff1744' : undefined }}
            >
              {formatNumber(totalErrors)}
            </span>
            <span className="dashboard-kpi__label">Errors</span>
          </div>
        </div>
      )}

      {/* Per-Model Summary */}
      {data && (
        <section className="view__section">
          <h2 className="view__section-title">Per-Model Summary</h2>
          <div className="data-table__wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="data-table__th">Model</th>
                  <th className="data-table__th">Invocations</th>
                  <th className="data-table__th">Input Tokens</th>
                  <th className="data-table__th">Output Tokens</th>
                  <th className="data-table__th">Avg Latency</th>
                </tr>
              </thead>
              <tbody>
                {data.models.map((modelId) => {
                  const cfg = configMap.get(modelId);
                  const s = data.summary[modelId];
                  if (!s) return null;
                  return (
                    <tr key={modelId} className="data-table__tr">
                      <td className="data-table__td">
                        <span
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: cfg?.color ?? '#888',
                            marginRight: 8,
                            verticalAlign: 'middle',
                          }}
                        />
                        {cfg?.label ?? modelId}
                      </td>
                      <td className="data-table__td">{formatNumber(s.invocations)}</td>
                      <td className="data-table__td">{formatNumber(s.inputTokens)}</td>
                      <td className="data-table__td">{formatNumber(s.outputTokens)}</td>
                      <td className="data-table__td">{formatLatency(s.avgLatencyMs)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Token Usage Chart (Area) */}
      {data && (
        <section className="view__section">
          <h2 className="view__section-title">Token Usage</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={buildTokenChartData(data)} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  {data.models.map((modelId) => {
                    const cfg = configMap.get(modelId);
                    const color = cfg?.color ?? '#888';
                    const safeId = modelId.replace(/\./g, '_');
                    return [
                      <linearGradient key={`${safeId}_input`} id={`grad-${safeId}-input`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                      </linearGradient>,
                      <linearGradient key={`${safeId}_output`} id={`grad-${safeId}-output`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.12} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.01} />
                      </linearGradient>,
                    ];
                  })}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2d4a" />
                <XAxis
                  dataKey="time"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={AXIS_LINE}
                  tickFormatter={(val: string) => formatTime(val, preset.hours)}
                />
                <YAxis
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={AXIS_LINE}
                  tickFormatter={(val: number) => formatNumber(val)}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#64748b' }}
                  labelFormatter={(val: any) => formatTime(val, preset.hours)}
                  formatter={(value: any, name: any) => {
                    const nameStr = String(name ?? '');
                    const parts = nameStr.split('_');
                    const type = parts[parts.length - 1];
                    const modelId = parts.slice(0, -1).join('_');
                    const cfg = configMap.get(modelId);
                    const label = cfg?.label ?? modelId;
                    const direction = type === 'input' ? 'Input' : 'Output';
                    return [formatNumber(value), `${label} ${direction}`];
                  }}
                />
                <Legend
                  formatter={(value: string) => {
                    const parts = value.split('_');
                    const type = parts[parts.length - 1];
                    const modelId = parts.slice(0, -1).join('_');
                    const cfg = configMap.get(modelId);
                    const label = cfg?.label ?? modelId;
                    const direction = type === 'input' ? 'Input' : 'Output';
                    const icon = type === 'input' ? '\u2501' : '\u2504';
                    return (
                      <span style={{ color: '#8b95a8', fontSize: 12 }}>
                        <span style={{ letterSpacing: 1, marginRight: 4, fontFamily: 'monospace' }}>{icon}</span>
                        {label} {direction}
                      </span>
                    );
                  }}
                />
                {data.models.map((modelId) => {
                  const cfg = configMap.get(modelId);
                  const color = cfg?.color ?? '#888';
                  const safeId = modelId.replace(/\./g, '_');
                  return [
                    <Area
                      key={`${modelId}_input`}
                      type="monotone"
                      dataKey={`${modelId}_input`}
                      stackId={modelId}
                      stroke={color}
                      strokeWidth={2}
                      fill={`url(#grad-${safeId}-input)`}
                      name={`${modelId}_input`}
                    />,
                    <Area
                      key={`${modelId}_output`}
                      type="monotone"
                      dataKey={`${modelId}_output`}
                      stackId={modelId}
                      stroke={color}
                      strokeWidth={1.5}
                      strokeDasharray="6 3"
                      fill={`url(#grad-${safeId}-output)`}
                      fillOpacity={0.4}
                      name={`${modelId}_output`}
                    />,
                  ];
                })}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Invocations Chart (Bar) */}
      {data && (
        <section className="view__section">
          <h2 className="view__section-title">Invocations</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={buildChartData(data, 'Invocations')} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2d4a" />
                <XAxis
                  dataKey="time"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={AXIS_LINE}
                  tickFormatter={(val: string) => formatTime(val, preset.hours)}
                />
                <YAxis
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={AXIS_LINE}
                  tickFormatter={(val: number) => formatNumber(val)}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#64748b' }}
                  labelFormatter={(val: any) => formatTime(val, preset.hours)}
                  formatter={(value: any) => [formatNumber(value), '']}
                />
                <Legend
                  formatter={(value: string) => {
                    const cfg = configMap.get(value);
                    return <span style={{ color: '#8b95a8', fontSize: 12 }}>{cfg?.label ?? value}</span>;
                  }}
                />
                {data.models.map((modelId) => {
                  const cfg = configMap.get(modelId);
                  return (
                    <Bar
                      key={modelId}
                      dataKey={modelId}
                      fill={cfg?.color ?? '#888'}
                      radius={[2, 2, 0, 0]}
                      name={modelId}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Average Latency Chart (Line) */}
      {data && (
        <section className="view__section">
          <h2 className="view__section-title">Average Latency</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={buildChartData(data, 'InvocationLatency')} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2d4a" />
                <XAxis
                  dataKey="time"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={AXIS_LINE}
                  tickFormatter={(val: string) => formatTime(val, preset.hours)}
                />
                <YAxis
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={AXIS_LINE}
                  tickFormatter={(val: number) => formatLatency(val)}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#64748b' }}
                  labelFormatter={(val: any) => formatTime(val, preset.hours)}
                  formatter={(value: any) => [formatLatency(value), '']}
                />
                <Legend
                  formatter={(value: string) => {
                    const cfg = configMap.get(value);
                    return <span style={{ color: '#8b95a8', fontSize: 12 }}>{cfg?.label ?? value}</span>;
                  }}
                />
                {data.models.map((modelId) => {
                  const cfg = configMap.get(modelId);
                  return (
                    <Line
                      key={modelId}
                      type="monotone"
                      dataKey={modelId}
                      stroke={cfg?.color ?? '#888'}
                      strokeWidth={2}
                      dot={false}
                      name={modelId}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Latency Comparison (p50) */}
      {data && (() => {
        // Calculate approximate p50 (median) from InvocationLatency time series for each model
        const latencyData: { modelId: string; label: string; color: string; p50: number }[] = [];

        data.models.forEach((modelId) => {
          const series = data.metrics[modelId]?.InvocationLatency;
          if (!series) return;
          // Filter out zero values for a more meaningful median
          const nonZero = series.values.filter((v) => v > 0);
          if (nonZero.length === 0) return;
          // Sort and take the median
          const sorted = [...nonZero].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          const p50 = sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid];

          const cfg = configMap.get(modelId);
          latencyData.push({
            modelId,
            label: cfg?.label ?? modelId,
            color: cfg?.color ?? '#888',
            p50,
          });
        });

        // Sort by fastest (lowest latency) first
        latencyData.sort((a, b) => a.p50 - b.p50);

        if (latencyData.length === 0) return null;

        const maxP50 = Math.max(...latencyData.map((d) => d.p50));

        return (
          <section className="view__section">
            <h2 className="view__section-title">Latency Comparison (p50)</h2>
            <div className="chart-container">
              {latencyData.map((item) => {
                const widthPct = maxP50 > 0 ? (item.p50 / maxP50) * 100 : 0;
                return (
                  <div key={item.modelId} className="latency-comparison__row">
                    <span className="latency-comparison__model">
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: item.color,
                          marginRight: 8,
                          verticalAlign: 'middle',
                        }}
                      />
                      {item.label}
                    </span>
                    <div className="latency-comparison__bar-wrapper">
                      <div
                        className="latency-comparison__bar"
                        style={{ width: `${widthPct}%`, background: item.color }}
                      />
                    </div>
                    <span className="latency-comparison__value">
                      {formatLatency(item.p50)}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* Throttles & Errors Chart (Bar) */}
      {data && (
        <section className="view__section">
          <h2 className="view__section-title">Throttles &amp; Errors</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={buildErrorChartData(data)} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2d4a" />
                <XAxis
                  dataKey="time"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={AXIS_LINE}
                  tickFormatter={(val: string) => formatTime(val, preset.hours)}
                />
                <YAxis
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={AXIS_LINE}
                  tickFormatter={(val: number) => formatNumber(val)}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#64748b' }}
                  labelFormatter={(val: any) => formatTime(val, preset.hours)}
                  formatter={(value: any) => [formatNumber(value), '']}
                />
                <Legend
                  formatter={(value: string) => {
                    const parts = value.split('_');
                    const modelId = parts.slice(0, -1).join('_');
                    const type = parts[parts.length - 1];
                    const cfg = configMap.get(modelId);
                    const label = cfg?.label ?? modelId;
                    return <span style={{ color: '#8b95a8', fontSize: 12 }}>{label} {type}</span>;
                  }}
                />
                {data.models.map((modelId) => {
                  return [
                    <Bar
                      key={`${modelId}_throttles`}
                      dataKey={`${modelId}_throttles`}
                      fill="#ffab00"
                      radius={[2, 2, 0, 0]}
                      name={`${modelId}_throttles`}
                    />,
                    <Bar
                      key={`${modelId}_errors`}
                      dataKey={`${modelId}_errors`}
                      fill="#ff1744"
                      radius={[2, 2, 0, 0]}
                      name={`${modelId}_errors`}
                    />,
                  ];
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
    </div>
  );
}
